import React from 'react';
import { BarChart3, Hourglass, Building2, Timer, CalendarClock, X } from 'lucide-react';
import {
    AgingBucket,
    CustomerInvoiceStats,
    CustomerPaymentBehaviour,
    IssuedGranularity,
    IssuedPeriodPoint,
    MonthlyInvoicePoint,
} from '../../services/invoiceStats';
import { Cents, formatCents } from '../../services/money';

// Rechnungs-Charts fuer das Cockpit. Handgebaut aus div/Flex im Stil von
// QuoteCharts – das Projekt zieht bewusst keine Chart-Library.
//
// Rein praesentational: alle Serien kommen fertig gerechnet aus invoiceStats.ts.
// Kein Date.now(), kein eigener Zugriff auf Belege.
//
// invoiceStats trennt Stichtags- von Flussgroessen, und diese Trennung muss hier
// sichtbar bleiben: die Altersstruktur beschreibt den Bestand von HEUTE und
// ignoriert den Zeitraumfilter, alle uebrigen Panels sind an das Rechnungsdatum
// im Fenster gebunden. Ohne den Hinweis in der Kopfzeile liest der Nutzer vier
// Panels als eine einzige Zeitraumauswertung.

const AS_OF_TODAY_HINT = 'Stand heute · unabhängig vom Zeitraum';

/** Kurzform fuer Balkenbeschriftungen – die volle Summe steht im Tooltip. */
const compactEur = (cents: Cents): string => {
    const euro = cents / 100;
    return Math.abs(euro) >= 1000 ? `${Math.round(euro / 1000)}k` : String(Math.round(euro));
};

/** Vorzeichen mitgefuehrt: "+4 Tage" und "-4 Tage" sind gegenteilige Aussagen. */
const fmtDelay = (days: number | null): string => {
    if (days === null) return '—';
    const rounded = Math.round(days) || 0;
    return `${rounded > 0 ? '+' : ''}${rounded} ${Math.abs(rounded) === 1 ? 'Tag' : 'Tage'}`;
};

const fmtDays = (days: number | null): string => {
    if (days === null) return '—';
    const rounded = Math.round(days);
    return `${rounded} ${rounded === 1 ? 'Tag' : 'Tage'}`;
};

const Panel: React.FC<{
    title: string;
    icon: React.ReactNode;
    hint?: string;
    /** Bedienelemente rechts in der Kopfzeile, z. B. ein Umschalter. */
    actions?: React.ReactNode;
    children: React.ReactNode;
}> = ({ title, icon, hint, actions, children }) => (
    <div className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden">
        <div className="px-5 py-3 border-b border-slate-100 flex flex-wrap items-center justify-between gap-3">
            <h3 className="text-sm font-bold text-slate-800 flex items-center gap-2">{icon} {title}</h3>
            <div className="flex flex-wrap items-center justify-end gap-3">
                {hint && <span className="text-[11px] text-slate-400 text-right">{hint}</span>}
                {actions}
            </div>
        </div>
        {children}
    </div>
);

const Empty: React.FC<{ text: string }> = ({ text }) => (
    <div className="py-12 text-center text-slate-300 text-xs font-medium">{text}</div>
);

// ─── Rechnungsvolumen je Monat ──────────────────────────────────────────────

const SERIES: { key: 'paid' | 'open' | 'overdue'; label: string; cls: string }[] = [
    { key: 'paid', label: 'Bezahlt', cls: 'bg-emerald-500' },
    { key: 'open', label: 'Offen', cls: 'bg-slate-300' },
    { key: 'overdue', label: 'Überfällig', cls: 'bg-red-500' },
];

/** Nur der positive Anteil laesst sich stapeln – siehe Kommentar im Chart. */
const stackHeight = (value: Cents): number => Math.max(0, value);

export const MonthlyInvoiceChart: React.FC<{ points: MonthlyInvoicePoint[]; periodLabel?: string }> = ({
    points,
    periodLabel,
}) => {
    const max = Math.max(
        1,
        ...points.map((p) => stackHeight(p.paid) + stackHeight(p.open) + stackHeight(p.overdue)),
    );

    return (
        <Panel
            title="Rechnungsvolumen je Monat"
            icon={<BarChart3 className="w-4 h-4 text-blue-500" />}
            hint={`Zahlbetrag je Rechnungsmonat${periodLabel ? ` · ${periodLabel}` : ''} · max. 24 Monate`}
        >
            {points.length === 0 ? (
                <Empty text="Keine ausgestellten Rechnungen im gewählten Zeitraum." />
            ) : (
                <div className="p-5">
                    <div className="flex flex-wrap gap-4 mb-4">
                        {SERIES.map((s) => (
                            <span key={s.key} className="flex items-center gap-1.5 text-[11px] font-bold text-slate-500">
                                <span className={`w-2.5 h-2.5 rounded-sm ${s.cls}`} /> {s.label}
                            </span>
                        ))}
                    </div>
                    <div className="overflow-x-auto">
                        <div className="flex items-end gap-2 min-w-max">
                            {points.map((p) => {
                                // Der beglichene Anteil kann negativ sein, wenn ein Monat
                                // ueberwiegend Storno enthaelt. Ein negativer Balken laesst
                                // sich nicht stapeln, deshalb bleibt das Segment leer – der
                                // echte Wert steht im Tooltip und in der Kopfzahl.
                                const total = p.paid + p.open + p.overdue;
                                return (
                                    <div key={p.month} className="flex flex-col items-center gap-1.5 w-12 shrink-0">
                                        <span className="text-[10px] font-bold text-slate-400 tabular-nums h-3">
                                            {total !== 0 ? compactEur(total) : ''}
                                        </span>
                                        <div
                                            className="w-full h-32 flex flex-col justify-end rounded-t-md overflow-hidden bg-slate-50"
                                            title={`${p.label} — bezahlt ${formatCents(p.paid)}, offen ${formatCents(p.open)}, überfällig ${formatCents(p.overdue)}`}
                                        >
                                            {SERIES.map((s) => {
                                                const v = stackHeight(p[s.key]);
                                                if (v <= 0) return null;
                                                return (
                                                    <div
                                                        key={s.key}
                                                        className={s.cls}
                                                        style={{ height: `${(v / max) * 100}%` }}
                                                    />
                                                );
                                            })}
                                        </div>
                                        <span className="text-[11px] font-bold text-slate-400 whitespace-nowrap">
                                            {p.label}
                                        </span>
                                    </div>
                                );
                            })}
                        </div>
                    </div>
                </div>
            )}
        </Panel>
    );
};

// ─── Altersstruktur der Forderungen ─────────────────────────────────────────

/**
 * Farbverlauf der Altersklassen. Absichtlich eskalierend: die Klasse allein
 * sagt noch nichts, erst der Farbsprung macht sichtbar, dass Geld liegen bleibt.
 */
const AGING_TONE: Record<AgingBucket['key'], string> = {
    '0-30': 'bg-emerald-500',
    '31-60': 'bg-amber-400',
    '61-90': 'bg-amber-500',
    '90+': 'bg-red-500',
};

export const ReceivablesAgingChart: React.FC<{ buckets: AgingBucket[] }> = ({ buckets }) => {
    const total = buckets.reduce((sum, b) => sum + b.cents, 0);
    const max = Math.max(1, ...buckets.map((b) => b.cents));

    return (
        <Panel
            title="Altersstruktur der Forderungen"
            icon={<Hourglass className="w-4 h-4 text-amber-500" />}
            hint={`Tage über Fälligkeit · ${AS_OF_TODAY_HINT}`}
        >
            {total === 0 ? (
                <Empty text="Keine offenen Forderungen." />
            ) : (
                <div className="p-5 space-y-3">
                    {/* Immer alle vier Klassen, auch leere: eine Achse, die je nach
                        Datenlage die Klassen wechselt, ist nicht vergleichbar. */}
                    {buckets.map((b) => (
                        <div key={b.key} className="flex items-center gap-3">
                            <span className="w-24 shrink-0 text-[11px] font-bold text-slate-500">{b.label}</span>
                            <div className="flex-1 h-8 bg-slate-50 rounded-lg overflow-hidden">
                                <div
                                    className={`h-full ${AGING_TONE[b.key]} rounded-lg transition-all`}
                                    style={{ width: `${b.cents > 0 ? Math.max(4, (b.cents / max) * 100) : 0}%` }}
                                />
                            </div>
                            <span className="w-36 shrink-0 text-right text-[11px] tabular-nums">
                                <span className="font-black text-slate-700">{b.count}</span>
                                <span className="text-slate-400"> · {formatCents(b.cents)}</span>
                            </span>
                        </div>
                    ))}
                    <p className="text-[11px] text-slate-400 pt-1">
                        Gesamt offen: <span className="font-bold text-slate-500">{formatCents(total)}</span>
                    </p>
                </div>
            )}
        </Panel>
    );
};

// ─── Top-Kunden nach Umsatz ─────────────────────────────────────────────────

export const TopInvoiceCustomersTable: React.FC<{ rows: CustomerInvoiceStats[]; periodLabel?: string }> = ({
    rows,
    periodLabel,
}) => (
    <Panel
        title="Top-Kunden nach Umsatz"
        icon={<Building2 className="w-4 h-4 text-emerald-500" />}
        hint={`netto je Rechnungsdatum${periodLabel ? ` · ${periodLabel}` : ''}`}
    >
        {rows.length === 0 ? (
            <Empty text="Keine ausgestellten Rechnungen im gewählten Zeitraum." />
        ) : (
            <div className="overflow-x-auto">
                <table className="w-full text-left border-collapse min-w-[560px]">
                    <thead>
                        <tr className="bg-slate-50 border-b border-slate-200 text-[11px] uppercase font-bold text-slate-500 tracking-wider">
                            <th className="px-5 py-3">Kunde</th>
                            <th className="px-5 py-3 text-right">Rechnungen</th>
                            <th className="px-5 py-3 text-right">Umsatz</th>
                            <th className="px-5 py-3 text-right">Vereinnahmt</th>
                            <th className="px-5 py-3 text-right">Davon offen</th>
                        </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-100">
                        {rows.map((r) => (
                            <tr key={r.customerId} className="hover:bg-slate-50/60 transition-colors">
                                <td className="px-5 py-3 text-sm font-bold text-slate-700 truncate max-w-[220px]">
                                    {r.name}
                                </td>
                                <td className="px-5 py-3 text-right text-sm text-slate-500 tabular-nums">
                                    {r.invoiceCount}
                                </td>
                                <td className="px-5 py-3 text-right text-sm font-bold text-emerald-600 tabular-nums whitespace-nowrap">
                                    {formatCents(r.revenueNetCents)}
                                </td>
                                <td className="px-5 py-3 text-right text-sm text-slate-500 tabular-nums whitespace-nowrap">
                                    {formatCents(r.collectedCents)}
                                </td>
                                <td
                                    className={`px-5 py-3 text-right text-sm font-bold tabular-nums whitespace-nowrap ${
                                        r.openCents > 0 ? 'text-slate-700' : 'text-slate-300'
                                    }`}
                                >
                                    {formatCents(r.openCents)}
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
        )}
    </Panel>
);

// ─── Zahlungsmoral ──────────────────────────────────────────────────────────

/**
 * Bewusst eine eigene Tabelle statt einer Spalte in den Top-Kunden: die beiden
 * Listen sind unterschiedlich sortiert und beantworten unterschiedliche Fragen.
 * Der groesste Kunde ist nicht automatisch der saeumigste – und genau diese
 * Kombination ist die interessante.
 */
export const PaymentBehaviourTable: React.FC<{
    rows: CustomerPaymentBehaviour[];
    periodLabel?: string;
    /**
     * Die Zahlungen sind noch unterwegs.
     *
     * Ohne sie ist `rows` leer — nicht weil niemand gezahlt hat, sondern weil
     * die Grundlage fehlt. Der leere Zustand behauptete dann "keine Rechnung
     * vollstaendig bezahlt", also eine Aussage ueber die Kunden. Deshalb hier
     * ein eigener, ehrlicher Zwischenzustand.
     */
    paymentsPending?: boolean;
}> = ({ rows, periodLabel, paymentsPending }) => (
    <Panel
        title="Zahlungsmoral je Kunde"
        icon={<Timer className="w-4 h-4 text-red-500" />}
        hint={`Ø Verzug ab Fälligkeit${periodLabel ? ` · ${periodLabel}` : ''}`}
    >
        {paymentsPending ? (
            <Empty text="Zahlungen werden geladen …" />
        ) : rows.length === 0 ? (
            <Empty text="Im gewählten Zeitraum wurde keine Rechnung vollständig bezahlt." />
        ) : (
            <div className="overflow-x-auto">
                <table className="w-full text-left border-collapse min-w-[520px]">
                    <thead>
                        <tr className="bg-slate-50 border-b border-slate-200 text-[11px] uppercase font-bold text-slate-500 tracking-wider">
                            <th className="px-5 py-3">Kunde</th>
                            <th className="px-5 py-3 text-right">Bezahlt</th>
                            <th className="px-5 py-3 text-right">Ø Verzug</th>
                            <th className="px-5 py-3 text-right">Ø Dauer</th>
                            <th className="px-5 py-3 text-right">Überfällig · heute</th>
                        </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-100">
                        {rows.map((r) => {
                            const late = (r.avgPaymentDelayDays ?? 0) > 0;
                            return (
                                <tr key={r.customerId} className="hover:bg-slate-50/60 transition-colors">
                                    <td className="px-5 py-3 text-sm font-bold text-slate-700 truncate max-w-[220px]">
                                        {r.name}
                                    </td>
                                    <td className="px-5 py-3 text-right text-sm text-slate-500 tabular-nums">
                                        {r.settledCount}
                                    </td>
                                    <td
                                        className={`px-5 py-3 text-right text-sm font-bold tabular-nums whitespace-nowrap ${
                                            late ? 'text-red-600' : 'text-emerald-600'
                                        }`}
                                    >
                                        {fmtDelay(r.avgPaymentDelayDays)}
                                    </td>
                                    <td className="px-5 py-3 text-right text-sm text-slate-500 tabular-nums whitespace-nowrap">
                                        {fmtDays(r.avgDaysToPayDays)}
                                    </td>
                                    <td
                                        className={`px-5 py-3 text-right text-sm font-bold tabular-nums whitespace-nowrap ${
                                            r.overdueCents > 0 ? 'text-red-600' : 'text-slate-300'
                                        }`}
                                    >
                                        {formatCents(r.overdueCents)}
                                    </td>
                                </tr>
                            );
                        })}
                    </tbody>
                </table>
            </div>
        )}
    </Panel>
);

// ─── Gesamtanordnung ────────────────────────────────────────────────────────

interface InvoiceChartsProps {
    monthly: MonthlyInvoicePoint[];
    aging: AgingBucket[];
    topCustomers: CustomerInvoiceStats[];
    paymentBehaviour: CustomerPaymentBehaviour[];
    /** Beschriftung des gewaehlten Zeitraums, z. B. "90 T". Nur fuer die Flusspanels. */
    periodLabel?: string;
    /**
     * Die Zahlungen sind noch unterwegs.
     *
     * Nur die Zahlungsmoral haengt an ihnen. Monatsvolumen, Altersstruktur und
     * Top-Kunden rechnen ausschliesslich aus den Summen der Belege und stehen
     * deshalb sofort — sie deswegen mit zu sperren, hiesse drei richtige Panels
     * wegen eines vierten zurueckzuhalten.
     */
    paymentsPending?: boolean;
}

export const InvoiceCharts: React.FC<InvoiceChartsProps> = ({
    monthly,
    aging,
    topCustomers,
    paymentBehaviour,
    periodLabel,
    paymentsPending,
}) => (
    <div className="space-y-4">
        <MonthlyInvoiceChart points={monthly} periodLabel={periodLabel} />
        <div className="grid grid-cols-1 xl:grid-cols-2 gap-4">
            <ReceivablesAgingChart buckets={aging} />
            <PaymentBehaviourTable
                rows={paymentBehaviour}
                periodLabel={periodLabel}
                paymentsPending={paymentsPending}
            />
        </div>
        <TopInvoiceCustomersTable rows={topCustomers} periodLabel={periodLabel} />
    </div>
);

// ─── Gestellt je Monat / Woche / Tag ────────────────────────────────────────

const GRANULARITIES: { key: IssuedGranularity; label: string; title: string }[] = [
    { key: 'month', label: 'Monat', title: 'Gestellt je Monat' },
    { key: 'week', label: 'Woche', title: 'Gestellt je Kalenderwoche' },
    { key: 'day', label: 'Tag', title: 'Gestellt je Tag' },
];

/**
 * Aufschluesselung der Kachel "Gestellt": dieselbe Groesse (Forderung brutto,
 * verankert am Rechnungsdatum), aufgeloest nach Monat, Kalenderwoche oder Tag.
 * Die Zeile des laufenden Monats ist zeichengleich mit der Kachel — siehe
 * issuedByPeriod.
 *
 * Tabelle statt Balkenchart: die Frage lautet "wie viel war es im Juli", und
 * darauf antwortet eine Zahl in einer Zeile, nicht eine Balkenhoehe. Der
 * Balken daneben ist nur Groessenvergleich. Steht NICHT in der Auswertung
 * darunter, sondern direkt an der Kachel — wer die Kachel nicht versteht,
 * findet die Erklaerung nicht hinter einem zweiten Link.
 */
export const IssuedBreakdownPanel: React.FC<{
    points: IssuedPeriodPoint[];
    granularity: IssuedGranularity;
    onGranularityChange: (granularity: IssuedGranularity) => void;
    onClose: () => void;
}> = ({ points, granularity, onGranularityChange, onClose }) => {
    const current = GRANULARITIES.find((g) => g.key === granularity) ?? GRANULARITIES[0];
    const max = Math.max(1, ...points.map((p) => Math.abs(p.cents)));
    const totalCents = points.reduce((sum, p) => sum + p.cents, 0);
    const totalCount = points.reduce((sum, p) => sum + p.count, 0);

    return (
        <Panel
            title={current.title}
            icon={<CalendarClock className="w-4 h-4 text-emerald-500" />}
            hint="Forderung brutto je Rechnungsdatum · nur Zeiträume mit Belegen"
            actions={
                <div className="flex items-center gap-2">
                    <div
                        className="flex rounded-lg border border-slate-200 bg-slate-50 p-0.5"
                        role="group"
                        aria-label="Zeiteinheit"
                    >
                        {GRANULARITIES.map((g) => (
                            <button
                                key={g.key}
                                type="button"
                                onClick={() => onGranularityChange(g.key)}
                                aria-pressed={g.key === granularity}
                                className={`px-2.5 py-1 rounded-md text-[11px] font-bold transition-colors ${
                                    g.key === granularity
                                        ? 'bg-white text-slate-800 shadow-sm'
                                        : 'text-slate-500 hover:text-slate-800'
                                }`}
                            >
                                {g.label}
                            </button>
                        ))}
                    </div>
                    <button
                        type="button"
                        onClick={onClose}
                        title="Schließen"
                        aria-label="Schließen"
                        className="p-1 rounded-md text-slate-400 hover:text-slate-700 hover:bg-slate-100"
                    >
                        <X className="w-4 h-4" />
                    </button>
                </div>
            }
        >
            {points.length === 0 ? (
                <Empty text="Noch keine ausgestellten Belege." />
            ) : (
                <div className="overflow-x-auto">
                    <table className="w-full text-left border-collapse min-w-[520px]">
                        <thead>
                            <tr className="bg-slate-50 border-b border-slate-200 text-[11px] uppercase font-bold text-slate-500 tracking-wider">
                                <th className="px-5 py-3">Zeitraum</th>
                                <th className="px-5 py-3 text-right">Belege</th>
                                <th className="px-5 py-3 text-right">Gestellt · brutto</th>
                                <th className="px-5 py-3 w-1/3">
                                    <span className="sr-only">Anteil am größten Zeitraum</span>
                                </th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-slate-100">
                            {points.map((p) => (
                                <tr key={p.key} className="hover:bg-slate-50/60 transition-colors">
                                    <td className="px-5 py-2.5 text-sm font-bold text-slate-700 whitespace-nowrap">
                                        {p.label}
                                        {p.running && (
                                            <span className="ml-2 rounded-full bg-emerald-50 px-2 py-0.5 text-[10px] font-bold text-emerald-700">
                                                läuft
                                            </span>
                                        )}
                                    </td>
                                    <td className="px-5 py-2.5 text-right text-sm text-slate-500 tabular-nums whitespace-nowrap">
                                        {p.count}
                                        {p.correctionCount > 0 && (
                                            <span className="text-[11px] text-slate-400">
                                                {' '}· {p.correctionCount} Storno/Gutschrift
                                            </span>
                                        )}
                                    </td>
                                    <td
                                        className={`px-5 py-2.5 text-right text-sm font-bold tabular-nums whitespace-nowrap ${
                                            p.cents < 0 ? 'text-red-600' : 'text-slate-800'
                                        }`}
                                    >
                                        {formatCents(p.cents)}
                                    </td>
                                    <td className="px-5 py-2.5">
                                        {/* Nur der Betrag zaehlt fuer die Laenge; ein Storno-Monat
                                            liegt im Minus und bekommt denselben Balken in Rot. */}
                                        <div className="h-2 rounded-full bg-slate-100 overflow-hidden">
                                            <div
                                                className={`h-full rounded-full ${p.cents < 0 ? 'bg-red-400' : 'bg-emerald-500'}`}
                                                style={{ width: `${Math.max(2, (Math.abs(p.cents) / max) * 100)}%` }}
                                            />
                                        </div>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                        <tfoot>
                            <tr className="border-t border-slate-200 bg-slate-50 text-[11px] text-slate-500">
                                <td className="px-5 py-2 font-bold">
                                    Summe · {points.length} {points.length === 1 ? 'Zeitraum' : 'Zeiträume'}
                                </td>
                                <td className="px-5 py-2 text-right tabular-nums">{totalCount}</td>
                                <td className="px-5 py-2 text-right font-bold tabular-nums text-slate-700 whitespace-nowrap">
                                    {formatCents(totalCents)}
                                </td>
                                <td />
                            </tr>
                        </tfoot>
                    </table>
                </div>
            )}
        </Panel>
    );
};
